import { useState, useEffect } from "react";
import { Divider, Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import useBreakPoinst from "@hooks/useBreakPoinst";
import {
  ModalBox,
  ActionContainer,
  StyledButton,
  CloseButtonContainer,
  SectionWrapper,
  TextContainer,
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
  FeaturesWrapper,
  FeatureItem,
  TimeSavedRow,
  TimeSavedHighlight,
  ImageContainer,
  StyledCloseIcon,
  StyledAutomationImg,
} from "./styles/AutomateReservationsModal";
import { useAutomation } from "@services";
import FeaturesPlaceholder from "./FeaturesPlaceholder";
import { useOnboarding } from "../../../../services/useOnboarding";

interface ConnectAirbnbModalProps {
  open: boolean;
  onClose: () => void;
  handleNextModal?: () => void;
  onRefetchOnboarding?: () => void;
}

const AutomateReservationsModal = ({
  open,
  onClose,
  handleNextModal,
  onRefetchOnboarding,
}: ConnectAirbnbModalProps) => {
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const { isPhone } = useBreakPoinst();
  const { t } = useTranslation();

  const { GetAllAutomationsQuery } = useAutomation();
  const { data, isLoading } = GetAllAutomationsQuery({
    pageSize: "10",
    pageNumber: "1",
  });
  // console.log(data?.result?.automations);
  const features = data?.result?.automations;

  const toggleFeature = (key: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const { updateActivitiesMutate } = useOnboarding();
  const { mutate: updateActivities, isSuccess } = updateActivitiesMutate({
    onSuccess: () => {},
    onError: (error) => {},
  });

  // Revalidate main onboarding query AFTER API is fulfilled
  useEffect(() => {
    if (isSuccess) {
      onRefetchOnboarding?.();
    }
  }, [isSuccess, onRefetchOnboarding]);
  const handleUpdateActivities = async () => {
    await updateActivities(selectedFeatures);
    handleNextModal?.();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="connect-airbnb-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ModalBox $isPhone={isPhone}>
        {/* Show close button at the top for phone screens */}
        {isPhone && (
          <CloseButtonContainer onClick={onClose}>
            <StyledCloseIcon />
          </CloseButtonContainer>
        )}
        <SectionWrapper>
          {/* Left Text Section */}
          <TextContainer>
            <HeaderContainer>
              <HeaderTitle>{t("onBoardingJourny.automationInfo")}</HeaderTitle>
              <HeaderSubtitle>
                {t("onBoardingJourny.chooseTasksToAutomate")}
              </HeaderSubtitle>
              <Divider />
            </HeaderContainer>

            <FeaturesWrapper>
              {isLoading ? (
                <FeaturesPlaceholder count={5} />
              ) : (
                features?.map((feature: any, key: number) => (
                  <FeatureItem
                    key={feature?.id ?? key}
                    onClick={() => toggleFeature(feature?.id)}
                    selected={selectedFeatures.includes(feature?.id)}
                  >
                    {feature?.name}
                  </FeatureItem>
                ))
              )}
            </FeaturesWrapper>

            <TimeSavedRow>
              {t("onBoardingJourny.minutesSaved")}
              <TimeSavedHighlight>
                {t("onBoardingJourny.minutesSaved_plural")}
              </TimeSavedHighlight>
            </TimeSavedRow>
          </TextContainer>

          {/* Right Image Section */}
          {!isPhone && (
            <ImageContainer>
              <CloseButtonContainer onClick={onClose}>
                <StyledCloseIcon />
              </CloseButtonContainer>
              <StyledAutomationImg />
            </ImageContainer>
          )}
        </SectionWrapper>

        <Divider />

        <ActionContainer $isPhone={isPhone}>
          <StyledButton
            $TextColor="white"
            $isPhone={isPhone}
            $BgColor="#5B3FFF"
            $width="85px"
            onClick={handleUpdateActivities}
          >
            {t("onBoardingJourny.activate")}
          </StyledButton>
        </ActionContainer>
      </ModalBox>
    </Modal>
  );
};

export default AutomateReservationsModal;
